// tabs/GeneralStatisticsTab.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronUp, ChevronDown, Check } from '@gravity-ui/icons';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie, Legend,
} from 'recharts';
import { getCamera } from '../../../services/cameras';
import {
  getCameraStatistics,
  updateCameraFteConst,
} from '../../../services/statistics';
import { DateTimeInput } from '../../../components/DateTimeInput';
import ScopeFilter from '../../../components/ScopeFilter/ScopeFilter';
import useScopeOptions from '../../../components/ScopeFilter/useScopeOptions';
import { useScopeCache } from '../../../hooks/useScopeCache';
import { useDraftScope } from '../../../hooks/useDraftScope';
import { resolveScopeCameras } from '../../../components/ScopeFilter/scopeCameras';
import { describeSingleTimezone } from '../../../utils/cameraTimezone';
import { countRu, CAMERA_FORMS } from '../../../utils/pluralRu';
import { MODES, formatValue } from '../../../components/DateTimeInput/valueFormat';
import './GeneralStatisticsTab.css';

// ─── constants ────────────────────────────────────────────────────────────────
const SHIFT_DEFS = [
  { id: 'SHIFT_1', label: 'Смена 1', startKey: 'shift1_start', endKey: 'shift1_end' },
  { id: 'SHIFT_2', label: 'Смена 2', startKey: 'shift2_start', endKey: 'shift2_end' },
  { id: 'SHIFT_3', label: 'Смена 3', startKey: 'shift3_start', endKey: 'shift3_end' },
];

/** §19.3: блоков за раз — ограниченный набор, а не «сколько выбрано». */
const MAX_STAT_BLOCKS = 6;
const SCOPE_CACHE_KEY = 'statistics:general';

const PRESENCE_COLORS = ['#b0bec5', '#42a5f5', '#26c6da', '#66bb6a', '#ffa726', '#ef5350'];
const COLOR_OK = '#4CAF50';
const COLOR_LATE = '#ef5350';
const COLOR_FILL = '#e0e0e0';

const HelpLink = ({ to, children }) => (
  <Link className="gs-help-link" to={to}>
    {children}
  </Link>
);

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmtMin = (sec) => {
  if (sec == null || isNaN(sec)) return '—';
  const totalMin = Math.round(sec / 60);
  if (totalMin === 0) return '0:00';
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

const fmtTime = (t) => (t ? t.slice(0, 5) : '—'); // "10:50:22" → "10:50"

/**
 * FastAPI отдаёт detail строкой, но на 422 — массивом объектов вида
 * { loc, msg, type }. Такой detail нельзя положить в state и отрендерить:
 * React падает с "Objects are not valid as a React child".
 */
const extractErrorMessage = (error, fallback = 'Ошибка загрузки данных') => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => (typeof d === 'string' ? d : d?.msg)).filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  }
  if (typeof detail?.msg === 'string') return detail.msg;
  return fallback;
};

const getAvailableShifts = (cam) =>
  cam ? SHIFT_DEFS.filter((s) => cam[s.startKey] != null && cam[s.endKey] != null) : [];

/**
 * Возвращает строку datetime-local в ЛОКАЛЬНОМ времени.
 * d.toISOString().slice(0,16) даёт UTC — это баг при отображении в инпутах.
 */
const toLocalDT = (date) => formatValue(MODES.DATETIME, new Date(date), false);

// ─── YAxis style for duration axes ───────────────────────────────────────────
/**
 * Стандартный YAxis для графиков продолжительности.
 * Отображает значения в формате чч:мм.
 * Метка "чч:мм" размещается сверху оси.
 */
const DurationYAxis = ({ width = 60, ...props }) => (
  <YAxis
    tickFormatter={fmtMin}
    tick={{ fontSize: 11 }}
    width={width}
    label={{
      value: 'чч:мм',
      position: 'insideTopLeft',
      offset: 2,
      dy: -4,
      style: { fontSize: 10, fill: '#aaa', textAnchor: 'start' },
    }}
    {...props}
  />
);

// ─── ShiftDropdown ────────────────────────────────────────────────────────────
const ShiftDropdown = ({ availableShifts, selectedShifts, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle = (id) =>
    onChange(selectedShifts.includes(id)
      ? selectedShifts.filter((s) => s !== id)
      : [...selectedShifts, id]);

  const label = availableShifts.length === 0 ? 'Нет смен'
    : selectedShifts.length === 0 ? 'Не выбрано'
      : selectedShifts.length === availableShifts.length ? 'Все смены'
        : availableShifts.filter((s) => selectedShifts.includes(s.id)).map((s) => s.label).join(', ');

  return (
    <div className="gs-shift-dropdown" ref={ref}>
      <button type="button"
        className={`gs-shift-trigger form-control${open ? ' gs-shift-trigger--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        disabled={availableShifts.length === 0}>
        <span className="gs-shift-trigger-text">{label}</span>
        <span className="gs-shift-arrow">{open ? <ChevronUp width={14} height={14} /> : <ChevronDown width={14} height={14} />}</span>
      </button>
      {open && (
        <div className="gs-shift-panel">
          {availableShifts.map((s) => (
            <label key={s.id} className="gs-shift-option">
              <input type="checkbox"
                checked={selectedShifts.includes(s.id)}
                onChange={() => toggle(s.id)} />
              <span>{s.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Tooltip for duration values ──────────────────────────────────────────────
const MinTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="gs-tooltip">
      <p className="gs-tooltip-label">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.fill || p.color, margin: '2px 0' }}>
          {p.name}:{' '}
          <strong>{fmtMin(p.value)}</strong>
          <span style={{ fontSize: 11, color: '#aaa', marginLeft: 4 }}>чч:мм</span>
        </p>
      ))}
    </div>
  );
};

// ─── PieLabel ─────────────────────────────────────────────────────────────────
const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const R = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  return (
    <text x={cx + r * Math.cos(-midAngle * R)} y={cy + r * Math.sin(-midAngle * R)}
      fill="white" textAnchor="middle" dominantBaseline="central" fontSize={13} fontWeight={600}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// ─── FTE Coverage Chart ───────────────────────────────────────────────────────
const FteCoverageChart = ({ displayStats, displayShifts }) => {
  if (!displayStats) return null;
  const norm = displayStats.fte_norm;
  if (!norm || norm <= 0) return null;

  if (displayShifts.length === 1) {
    const fact = displayStats.fte_total_fact ?? 0;
    const covered = parseFloat(Math.min(fact, norm).toFixed(2));
    const uncovered = parseFloat(Math.max(norm - fact, 0).toFixed(2));
    const pieData = [
      { name: 'Процент выполнения FTE', value: covered },
      { name: 'Недостаток', value: uncovered },
    ];
    return (
      <div className="gs-coverage-chart">
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={pieData} cx="50%" cy="50%" outerRadius={72}
              dataKey="value" labelLine={false} label={PieLabel}>
              <Cell fill={COLOR_OK} />
              <Cell fill={COLOR_FILL} />
            </Pie>
            <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const shiftFte = {};
  (displayStats.days ?? []).forEach((d) => {
    if (!shiftFte[d.shift]) shiftFte[d.shift] = { total: 0, count: 0 };
    shiftFte[d.shift].total += d.fte_fact;
    shiftFte[d.shift].count += 1;
  });
  const barData = displayShifts
    .filter((id) => shiftFte[id])
    .map((id) => {
      const avg = shiftFte[id].total / shiftFte[id].count;
      const cov = parseFloat(Math.min(avg, norm).toFixed(2));
      const rest = parseFloat(Math.max(norm - avg, 0).toFixed(2));
      return { name: SHIFT_DEFS.find((s) => s.id === id)?.label ?? id, covered: cov, uncovered: rest };
    });

  return (
    <div className="gs-coverage-chart">
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={barData} margin={{ top: 10, right: 16, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 11 }} width={40} />
          <Tooltip formatter={(v, name) => [v.toFixed(2), name === 'covered' ? 'Процент выполнения FTE' : 'Недостаток']} />
          <Legend formatter={(v) => v === 'covered' ? 'Процент выполнения FTE' : 'Недостаток'} iconType="circle" iconSize={10} />
          <Bar dataKey="covered" stackId="a" fill={COLOR_OK} radius={[0, 0, 0, 0]} />
          <Bar dataKey="uncovered" stackId="a" fill={COLOR_FILL} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Заголовок карточки одинаков во всех состояниях блока «Приход и уход».
const AttendanceCard = ({ children }) => (
  <div className="gs-card">
    <div className="gs-card-title-row">
      <h3 className="gs-card-title">Приход и уход</h3>
      <HelpLink to="/help?tab=attendance">
        Как контролировать время прихода и ухода сотрудников
      </HelpLink>
    </div>
    {children}
  </div>
);

// ─── Arrival/Departure Block ──────────────────────────────────────────────────
const ArrivalDepartureBlock = ({ displayStats, displayShifts, cameraData }) => {
  if (!displayStats?.days?.length) return null;

  const isSingle = displayShifts.length === 1;

  if (isSingle) {
    const shiftId = displayShifts[0];
    const shiftDef = SHIFT_DEFS.find((s) => s.id === shiftId);
    const days = displayStats.days.filter((d) => d.shift === shiftId);
    const normArrival = cameraData?.[shiftDef?.startKey] ?? null;
    const normDeparture = cameraData?.[shiftDef?.endKey] ?? null;

    // Продолжительность одной смены (норматив), в секундах.
    const shiftDurSec = (() => {
      if (!normArrival || !normDeparture) return null;
      const [ah, am] = normArrival.split(':').map(Number);
      const [dh, dm] = normDeparture.split(':').map(Number);
      const diff = (dh * 3600 + dm * 60) - (ah * 3600 + am * 60);
      return diff > 0 ? diff : null;
    })();

    // По столбцу на каждый день выбранной смены — дни не суммируются.
    // Высота столбца = норматив смены, он разбит на отработанное и потерянное время.
    // Если норматив неизвестен (не задан или смена через полночь) — рисуем только потери.
    const arrivalBarData = days.map((d) => {
      const lost = d.arrival_late_sec ?? 0;
      return {
        name: d.date,
        worked: shiftDurSec ? Math.max(shiftDurSec - lost, 0) : 0,
        lost,
      };
    });
    const departureBarData = days.map((d) => {
      const lost = d.departure_early_sec ?? 0;
      return {
        name: d.date,
        worked: shiftDurSec ? Math.max(shiftDurSec - lost, 0) : 0,
        lost,
      };
    });

    // При нескольких днях подписи оси X наклоняем, чтобы даты не слипались.
    const isMultiDay = days.length > 1;
    const chartHeight = isMultiDay ? 285 : 210;
    const chartMargin = { top: 8, right: 12, bottom: isMultiDay ? 40 : 4, left: 0 };
    const xAxisProps = isMultiDay
      ? { tick: { fontSize: 10 }, angle: -30, textAnchor: 'end', interval: 0 }
      : { tick: { fontSize: 12 } };
    // Наклонённые даты выходят за пределы оси, поэтому легенду опускаем ниже.
    const legendProps = {
      verticalAlign: 'bottom',
      wrapperStyle: { fontSize: 12, paddingTop: isMultiDay ? 50 : 0 },
    };

    const renderCaption = (lossLabel) => (
      <div className="gs-chart-caption">
        {shiftDurSec
          ? <>Из норматива смены <strong>{fmtMin(shiftDurSec)}</strong>: отработано и потеряно ({lossLabel})</>
          : <>Норматив смены не задан — показаны только потери ({lossLabel})</>}
      </div>
    );

    if (!days.length) {
      return (
        <AttendanceCard>
          <div className="gs-empty">
            Нет данных за выбранный период для смены «{shiftDef?.label ?? shiftId}»
          </div>
        </AttendanceCard>
      );
    }

    return (
      <AttendanceCard>
        <div className="gs-ad-single">
          {/* Arrivals */}
          <div className="gs-ad-half">
            <div className="gs-subchart-title">Приход на работу</div>
            <div className="gs-table-scroll" style={{ marginBottom: 14 }}>
              <table className="gs-table">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Факт</th>
                    <th>Норматив</th>
                    {/* Unit shown in header */}
                    <th>Опоздание <span className="gs-muted">(чч:мм)</span></th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => (
                    <tr key={d.date + d.shift}>
                      <td className="gs-td-head">{d.date}</td>
                      <td>{fmtTime(d.arrival_fact)}</td>
                      <td>{fmtTime(normArrival)}</td>
                      <td className={Math.round((d.arrival_late_sec ?? 0) / 60) > 0 ? 'gs-td-red' : ''}>
                        {fmtMin(d.arrival_late_sec)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderCaption('опоздание')}
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={arrivalBarData} margin={chartMargin}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" {...xAxisProps} />
                <DurationYAxis />
                <Tooltip content={<MinTooltip />} />
                <Legend iconType="circle" iconSize={9} {...legendProps} />
                {shiftDurSec && (
                  <Bar dataKey="worked" stackId="a" fill={COLOR_OK} name="Отработано в смене" radius={[0, 0, 0, 0]} />
                )}
                <Bar dataKey="lost" stackId="a" fill={COLOR_LATE} name="Опоздание" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Departures */}
          <div className="gs-ad-half">
            <div className="gs-subchart-title">Уход с работы</div>
            <div className="gs-table-scroll" style={{ marginBottom: 14 }}>
              <table className="gs-table">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Факт</th>
                    <th>Норматив</th>
                    <th>Уход раньше <span className="gs-muted">(чч:мм)</span></th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => (
                    <tr key={d.date + d.shift}>
                      <td className="gs-td-head">{d.date}</td>
                      <td>{fmtTime(d.departure_fact)}</td>
                      <td>{fmtTime(normDeparture)}</td>
                      <td className={Math.round((d.departure_early_sec ?? 0) / 60) > 0 ? 'gs-td-red' : ''}>
                        {fmtMin(d.departure_early_sec)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderCaption('уход раньше')}
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={departureBarData} margin={chartMargin}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" {...xAxisProps} />
                <DurationYAxis />
                <Tooltip content={<MinTooltip />} />
                <Legend iconType="circle" iconSize={9} {...legendProps} />
                {shiftDurSec && (
                  <Bar dataKey="worked" stackId="a" fill={COLOR_OK} name="Отработано в смене" radius={[0, 0, 0, 0]} />
                )}
                <Bar dataKey="lost" stackId="a" fill={COLOR_LATE} name="Уход раньше" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </AttendanceCard>
    );
  }

  // ── Multiple shifts ──────────────────────────────────────────────────────────
  const filteredDays = displayStats.days.filter((d) => displayShifts.includes(d.shift));
  const chartData = filteredDays.map((d) => ({
    name: `${d.date} ${SHIFT_DEFS.find((s) => s.id === d.shift)?.label ?? d.shift}`,
    'Опоздание': d.arrival_late_sec ?? 0,
    'Уход раньше': d.departure_early_sec ?? 0,
  }));

  if (!filteredDays.length) {
    return (
      <AttendanceCard>
        <div className="gs-empty">Нет данных за выбранный период для выбранных смен</div>
      </AttendanceCard>
    );
  }

  return (
    <AttendanceCard>
      <div className="gs-table-scroll" style={{ marginBottom: 16 }}>
        <table className="gs-table">
          <thead>
            <tr>
              <th>Дата смены</th>
              <th>Номер смены</th>
              <th>Опоздание <span className="gs-muted">(чч:мм)</span></th>
              <th>Уход раньше <span className="gs-muted">(чч:мм)</span></th>
            </tr>
          </thead>
          <tbody>
            {filteredDays.map((d) => (
              <tr key={d.date + d.shift}>
                <td className="gs-td-head">{d.date}</td>
                <td>{SHIFT_DEFS.find((s) => s.id === d.shift)?.label ?? d.shift}</td>
                <td className={Math.round((d.arrival_late_sec ?? 0) / 60) > 0 ? 'gs-td-red' : ''}>
                  {fmtMin(d.arrival_late_sec)}
                </td>
                <td className={Math.round((d.departure_early_sec ?? 0) / 60) > 0 ? 'gs-td-red' : ''}>
                  {fmtMin(d.departure_early_sec)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} margin={{ top: 10, right: 16, bottom: 40, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
          <DurationYAxis />
          <Tooltip content={<MinTooltip />} />
          <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 12, paddingTop: 56 }} />
          <Bar dataKey="Опоздание" fill={COLOR_LATE} radius={[3, 3, 0, 0]} />
          <Bar dataKey="Уход раньше" fill="#ff9800" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </AttendanceCard>
  );
};

// ─── FTE Calculator ───────────────────────────────────────────────────────────
const FteCalculator = () => {
  const [employees, setEmployees] = useState('');
  const [netTime, setNetTime] = useState('');
  const [shiftDuration, setShiftDuration] = useState('');

  const result = (() => {
    const e = parseFloat(employees);
    const n = parseFloat(netTime);
    const s = parseFloat(shiftDuration);
    if (!isNaN(e) && !isNaN(n) && !isNaN(s) && s > 0) {
      return (e * n / s).toFixed(1);
    }
    return null;
  })();

  return (
    <div className="gs-calc-card">
      <div className="gs-calc-header">
        <div className="gs-calc-title">Калькулятор FTE</div>
        <HelpLink to="/help?tab=fte">Как рассчитать FTE смены</HelpLink>
        <HelpLink to="/help?tab=delivery-optimization">Оптимизация численности персонала доставки</HelpLink>
      </div>
      <div className="gs-calc-body">
        <div className="gs-calc-fraction">
          {/* Числитель */}
          <div className="gs-calc-numerator">
            <div className="gs-calc-input-group">
              <input
                type="number"
                className="gs-calc-input"
                placeholder="Сотр."
                min="0"
                value={employees}
                onChange={(e) => setEmployees(e.target.value)}
                title="Количество сотрудников в смене"
              />
            </div>
            <span className="gs-calc-op">×</span>
            <div className="gs-calc-input-group">
              <input
                type="number"
                className="gs-calc-input"
                placeholder="ЧРВ"
                min="0"
                value={netTime}
                onChange={(e) => setNetTime(e.target.value)}
                title="Чистое рабочее время (в минутах)"
              />
            </div>
          </div>

          {/* Черта дроби */}
          <div className="gs-calc-line" />

          {/* Знаменатель */}
          <div className="gs-calc-denominator">
            <div className="gs-calc-input-group">
              <input
                type="number"
                className="gs-calc-input"
                placeholder="Длит."
                min="1"
                value={shiftDuration}
                onChange={(e) => setShiftDuration(e.target.value)}
                title="Длительность смены (в минутах)"
              />
            </div>
          </div>
        </div>

        {/* Знак равенства и результат */}
        <div className="gs-calc-result-block">
          <span className="gs-calc-eq">=</span>
          <div className={`gs-calc-result${result !== null ? ' gs-calc-result--filled' : ''}`}>
            {result !== null ? result : '—'}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Блок статистики одной камеры ────────────────────────────────────────────
/*
 * §19.3 (FE-FIX16): «Если текущий Statistics endpoint умеет только
 * single-camera/single-scope, несколько выбранных scopes отображать как
 * отдельные backend-derived blocks, а не вычислять aggregate на клиенте».
 *
 * Поэтому блок — единица отображения: своя камера, свой запрос
 * GET /cameras/{id}/statistics, свои смены (у камер они настроены по-разному) и
 * своё целевое FTE. Ни FTE, ни непрерывность, ни опоздания между блоками не
 * складываются: authoritative-агрегата по нескольким камерам в контракте нет.
 */
const CameraStatisticsBlock = ({ cameraId, cameraName, parentName, fromIso, toIso, showCalculator = false }) => {
  const [cameraData, setCameraData] = useState(null);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [displayStats, setDisplayStats] = useState(null);
  const [displayShifts, setDisplayShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [fteValue, setFteValue] = useState('');
  const [fteSaving, setFteSaving] = useState(false);
  const [fteSaved, setFteSaved] = useState(false);
  const [fteError, setFteError] = useState(false);

  // Последний запрос выигрывает: ответ по прежнему периоду или прежним сменам
  // не должен дорисоваться поверх актуального (§19.4).
  const requestRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    setCameraData(null);
    getCamera(cameraId).then((cam) => {
      if (cancelled) return;
      setCameraData(cam);
      setFteValue(cam.fte_const != null ? String(cam.fte_const) : '');
      setSelectedShifts(getAvailableShifts(cam).map((s) => s.id));
    }).catch((e) => {
      if (!cancelled) setError(extractErrorMessage(e, 'Не удалось загрузить камеру'));
    });
    return () => { cancelled = true; };
  }, [cameraId]);

  const shiftsKey = selectedShifts.join(',');

  useEffect(() => {
    if (!cameraData || !fromIso || !toIso || selectedShifts.length === 0) {
      setDisplayStats(null);
      return;
    }
    const token = requestRef.current + 1;
    requestRef.current = token;
    setLoading(true);
    setError(null);
    const shifts = shiftsKey.split(',');
    getCameraStatistics(cameraId, fromIso, toIso, shifts)
      .then((data) => {
        if (token !== requestRef.current) return;
        setDisplayStats(data);
        setDisplayShifts(shifts);
      })
      .catch((e) => {
        if (token !== requestRef.current) return;
        setDisplayStats(null);
        setError(extractErrorMessage(e));
      })
      .finally(() => {
        if (token === requestRef.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, cameraData, fromIso, toIso, shiftsKey]);

  const availableShifts = getAvailableShifts(cameraData);

  const handleFteSave = async () => {
    if (!cameraData) return;
    const parsed = fteValue === '' ? null : parseFloat(fteValue);
    if (fteValue !== '' && isNaN(parsed)) return;
    setFteSaving(true); setFteSaved(false); setFteError(false);
    try {
      await updateCameraFteConst(cameraId, cameraData.owner, parsed);
      const cam = await getCamera(cameraId);
      setCameraData(cam);
      setFteValue(cam.fte_const != null ? String(cam.fte_const) : '');
      if (displayStats) {
        const data = await getCameraStatistics(cameraId, fromIso, toIso, displayShifts);
        setDisplayStats(data);
      }
      setFteSaved(true);
    } catch {
      setFteError(true);
    } finally {
      setFteSaving(false);
    }
  };

  const presenceRows = displayStats?.presence ?? [];
  const presenceChartData = presenceRows.map((r) => ({
    name: `${r.people_count} чел.`, seconds: r.duration_seconds,
  }));

  const fteFact = displayStats?.fte_total_fact;
  const fteNorm = displayStats?.fte_norm;
  const fteBelowNorm = fteFact != null && fteNorm != null && fteFact < fteNorm;

  return (
    <div className="gs-block" data-testid="gs-camera-block">
      <div className="gs-card gs-card--full">
        <div className="gs-block-head">
          <h3 className="gs-card-title">{cameraName}</h3>
          {parentName && <span className="gs-block-parent">{parentName}</span>}
          {/* ТЗ №2 §10.3–10.4 и §19.3: время статистики — только зона камеры. */}
          {cameraData && (
            <span className="gs-timezone-note">{describeSingleTimezone(cameraData.display_timezone)}</span>
          )}
        </div>

        <div className="gs-row gs-row--wrap gs-row--align">
          <div className="gs-field">
            <label className="gs-label">
              Смены{availableShifts.length > 0 && (
                <span className="gs-shifts-count"> ({selectedShifts.length}/{availableShifts.length})</span>
              )}
            </label>
            <ShiftDropdown availableShifts={availableShifts}
              selectedShifts={selectedShifts} onChange={setSelectedShifts} />
          </div>
          {loading && <span className="gs-block-status" role="status">Загрузка…</span>}
        </div>

        {error && <div className="gs-error">{error}</div>}
        {!error && !loading && cameraData && selectedShifts.length === 0 && (
          <div className="gs-empty">Выберите хотя бы одну смену для этой камеры</div>
        )}
        {/* Нет данных у первой камеры — калькулятор всё равно нужен. */}
        {showCalculator && !displayStats && !loading && <FteCalculator />}

        {displayStats && (
          <>
            <h4 className="gs-card-subtitle">Присутствие персонала</h4>
            <div className="gs-presence-layout">
              <div className="gs-presence-left">
                <div className="gs-chart-box">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={presenceChartData} margin={{ top: 10, right: 16, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <DurationYAxis />
                      <Tooltip content={<MinTooltip />} />
                      <Bar dataKey="seconds" name="Время" radius={[3, 3, 0, 0]}>
                        {presenceChartData.map((_, i) => (
                          <Cell key={i} fill={PRESENCE_COLORS[Math.min(i, PRESENCE_COLORS.length - 1)]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="gs-table-scroll gs-table-full">
                  <table className="gs-table">
                    <thead>
                      <tr>
                        <th className="gs-th-row-label">Кол-во людей</th>
                        {presenceRows.map((r) => <th key={r.people_count}>{r.people_count}</th>)}
                        <th className={`gs-th-fte-combined ${fteBelowNorm ? 'gs-th-red' : 'gs-th-green'}`}>
                          FTE факт / Целевое FTE
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="gs-td-head">
                          Время <span className="gs-muted">(чч:мм)</span>
                        </td>
                        {presenceRows.map((r) => (
                          <td key={r.people_count}>{fmtMin(r.duration_seconds)}</td>
                        ))}
                        <td className={`gs-td-fte-combined ${fteBelowNorm ? 'gs-td-red' : 'gs-td-green'}`}>
                          <div className="gs-fte-combined">
                            <span className="gs-fte-fact-value">
                              {fteFact != null ? fteFact.toFixed(2) : '—'}
                            </span>
                            {cameraData ? (
                              <>
                                <span className="gs-fte-sep">/</span>
                                <input
                                  type="number" min="0" step="0.1"
                                  className="gs-fte-cell-input"
                                  aria-label={`Целевое FTE — ${cameraName}`}
                                  value={fteValue}
                                  onChange={(e) => { setFteValue(e.target.value); setFteSaved(false); setFteError(false); }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') handleFteSave(); }}
                                />
                                <button className="gs-fte-cell-btn" onClick={handleFteSave} disabled={fteSaving}
                                  title="Сохранить целевое FTE">
                                  {fteSaving ? '…' : <Check width={16} height={16} />}
                                </button>
                                {fteSaved && <span className="gs-badge gs-badge--ok"><Check width={14} height={14} /></span>}
                                {fteError && <span className="gs-badge gs-badge--err">!</span>}
                              </>
                            ) : (
                              fteNorm != null ? <span className="gs-muted">/ {fteNorm.toFixed(2)}</span> : null
                            )}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="gs-presence-right">
                <FteCoverageChart displayStats={displayStats} displayShifts={displayShifts} />
                {/* Калькулятор — справа от графика, как раньше; один на вкладку. */}
                {showCalculator && <FteCalculator />}
              </div>
            </div>
          </>
        )}
      </div>

      {displayStats && (
        <ArrivalDepartureBlock
          displayStats={displayStats}
          displayShifts={displayShifts}
          cameraData={cameraData}
        />
      )}
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
/*
 * FILTER-UX01 (ТЗ v4 §19.1/§19.3, FE-FIX16): тот же checkbox-компонент области
 * данных, что и на «Сводке», «Контроле» и Ганте, со своим кэшем состояния.
 *
 * Расчёт — по кнопке «Применить»: галочки и даты меняют черновик, и только
 * применённый выбор строит блоки. Блоки строятся лишь по явно отмеченным
 * камерам — выбранная локация не означает «посчитать все её камеры».
 * Кэш ключа `statistics:general` отдельный от ключа Ганты.
 */
export default function GeneralStatisticsTab() {
  const { scope, period, setScope, setPeriod } = useScopeCache(SCOPE_CACHE_KEY, {
    period: {
      from: (() => { const d = new Date(); d.setDate(d.getDate() - 7); return toLocalDT(d); })(),
      to: toLocalDT(new Date()),
    },
  });

  const scopeDraft = useDraftScope(scope, setScope);
  const [draftPeriod, setDraftPeriod] = useState({ from: period.from, to: period.to });
  const levels = useScopeOptions(scopeDraft.draft);
  const cameras = useMemo(() => resolveScopeCameras(scope, levels), [scope, levels]);

  const draftRangeValid = Boolean(draftPeriod.from && draftPeriod.to && draftPeriod.from < draftPeriod.to);
  const periodDirty = draftPeriod.from !== period.from || draftPeriod.to !== period.to;
  const applyAll = () => {
    scopeDraft.apply();
    setPeriod({ from: draftPeriod.from, to: draftPeriod.to });
  };

  const rangeValid = Boolean(period.from && period.to && period.from < period.to);
  const requested = useMemo(() => ({
    cameras: cameras.slice(0, MAX_STAT_BLOCKS),
    fromIso: rangeValid ? new Date(period.from).toISOString() : '',
    toIso: rangeValid ? new Date(period.to).toISOString() : '',
  }), [cameras, period.from, period.to, rangeValid]);

  return (
    <div className="gs-root">

      <div className="gs-card">
        <ScopeFilter
          value={scopeDraft.draft}
          onChange={scopeDraft.setDraft}
          onApply={applyAll}
          applyDisabled={!draftRangeValid || (!scopeDraft.dirty && !periodDirty)}
          levels={levels}
          idPrefix="gs-scope"
          title="Область данных"
        >
          <div className="gs-row gs-row--wrap gs-row--align">
            <div className="gs-field">
              <label className="gs-label">Начальная дата</label>
              <DateTimeInput value={draftPeriod.from}
                onChange={(value) => setDraftPeriod((prev) => ({ ...prev, from: value }))}
                ariaLabel="Начальная дата" />
            </div>
            <div className="gs-field">
              <label className="gs-label">Конечная дата</label>
              <DateTimeInput value={draftPeriod.to}
                onChange={(value) => setDraftPeriod((prev) => ({ ...prev, to: value }))}
                ariaLabel="Конечная дата" />
            </div>
          </div>
          {!draftRangeValid && (
            <div className="gs-error">Начальная дата должна быть раньше конечной.</div>
          )}
        </ScopeFilter>

        {cameras.length > MAX_STAT_BLOCKS && (
          <div className="gs-note">
            Выбрано {countRu(cameras.length, CAMERA_FORMS)}, показаны первые {MAX_STAT_BLOCKS}. Уточните выбор,
            чтобы увидеть остальные. Статистика по нескольким камерам не суммируется:
            у каждой камеры свой блок.
          </div>
        )}
      </div>

      {requested.cameras.map((camera, index) => (
        <CameraStatisticsBlock
          key={camera.id}
          cameraId={Number(camera.id)}
          cameraName={camera.name}
          parentName={camera.parentName}
          fromIso={requested.fromIso}
          toIso={requested.toIso}
          showCalculator={index === 0}
        />
      ))}

      {requested.cameras.length === 0 && (
        <>
          <div className="gs-empty">
            Отметьте камеры и нажмите «Применить» — статистика считается по камере,
            каждая камера отдельным блоком.
          </div>
          <div className="gs-card">
            <FteCalculator />
          </div>
        </>
      )}
    </div>
  );
}
